// Curated list of common timezones with display labels (US-first since MCL is US-based)
import { fmtDate, fmtTime } from '@/i18n'

export const COMMON_TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
  { value: 'America/Phoenix', label: 'Arizona (no DST)' },
  { value: 'America/Toronto', label: 'Eastern - Canada' },
  { value: 'America/Vancouver', label: 'Pacific - Canada' },
  { value: 'America/Edmonton', label: 'Mountain - Canada' },
  { value: 'America/Winnipeg', label: 'Central - Canada' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Central Europe (CET)' },
  { value: 'Europe/Istanbul', label: 'Istanbul (TRT)' },
  { value: 'Asia/Dubai', label: 'Dubai (GST)' },
  { value: 'Asia/Karachi', label: 'Pakistan (PKT)' },
  { value: 'Asia/Kolkata', label: 'India (IST)' },
  { value: 'Asia/Dhaka', label: 'Bangladesh (BST)' },
  { value: 'Asia/Kuala_Lumpur', label: 'Malaysia (MYT)' },
  { value: 'Asia/Tokyo', label: 'Japan (JST)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST)' },
  { value: 'Asia/Riyadh', label: 'Saudi Arabia (AST)' },
  { value: 'Africa/Cairo', label: 'Cairo (EET)' },
  { value: 'Africa/Lagos', label: 'West Africa (WAT)' },
] as const

export type TimezoneValue = (typeof COMMON_TIMEZONES)[number]['value']

/** Returns the browser's IANA timezone, falling back to America/Chicago */
export function getDefaultTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Chicago'
  } catch {
    return 'America/Chicago'
  }
}

/**
 * Default for a new event's timezone picker: the admin's browser zone when
 * it's one of COMMON_TIMEZONES (so the select can show it), else Central —
 * the same fallback the public pages use for a NULL zone.
 */
export function getDefaultEventTimezone(): string {
  const browser = getDefaultTimezone()
  return COMMON_TIMEZONES.some((tz) => tz.value === browser) ? browser : 'America/Chicago'
}

/** Extracts short timezone abbreviation (e.g., "CT", "ET") for a given date and timezone */
export function getTimezoneAbbreviation(date: Date, timezone: string): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short',
    }).formatToParts(date)
    return parts.find(p => p.type === 'timeZoneName')?.value || ''
  } catch {
    return ''
  }
}

interface DateTimeParts {
  date: string        // e.g., "Apr 17"
  time: string        // e.g., "12:00 PM"
  tzAbbrev: string    // e.g., "CT"
  localTime: string | null      // e.g., "2:00 PM" or null if same TZ
  localTzAbbrev: string | null  // e.g., "ET" or null if same TZ
}

/**
 * Formats an event datetime into granular parts for flexible display.
 * Returns the event time in its stored timezone plus the viewer's local equivalent.
 */
export function formatEventDateParts(
  isoDateStr: string | null,
  eventTimezone: string | null,
): DateTimeParts {
  if (!isoDateStr) {
    return { date: 'TBD', time: 'TBD', tzAbbrev: '', localTime: null, localTzAbbrev: null }
  }

  const d = new Date(isoDateStr)
  if (isNaN(d.getTime())) {
    return { date: 'TBD', time: 'TBD', tzAbbrev: '', localTime: null, localTzAbbrev: null }
  }

  const tz = eventTimezone || 'America/Chicago'
  const localTz = getDefaultTimezone()

  // Format in event timezone
  const date = fmtDate(d, { month: 'short', day: 'numeric', timeZone: tz })
  const time = fmtTime(d, tz)
  const tzAbbrev = getTimezoneAbbreviation(d, tz)

  // Format in viewer's timezone
  const localTimeStr = fmtTime(d, localTz)
  const localTzAbbr = getTimezoneAbbreviation(d, localTz)

  // Only show local conversion if different
  const isDifferent = time !== localTimeStr || tzAbbrev !== localTzAbbr
  return {
    date,
    time,
    tzAbbrev,
    localTime: isDifferent ? localTimeStr : null,
    localTzAbbrev: isDifferent ? localTzAbbr : null,
  }
}

/**
 * Formats an event datetime as a full string for display.
 * e.g., "Fri, Apr 17, 12:00 PM CT (2:00 PM ET your time)"
 */
export function formatEventDateTime(
  isoDateStr: string | null,
  eventTimezone: string | null,
  options?: { includeWeekday?: boolean },
): string {
  if (!isoDateStr) return 'TBD'

  const d = new Date(isoDateStr)
  if (isNaN(d.getTime())) return 'TBD'

  const tz = eventTimezone || 'America/Chicago'
  const localTz = getDefaultTimezone()

  const dateOpts: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: tz,
  }
  if (options?.includeWeekday) {
    dateOpts.weekday = 'long'
  }

  const datePart = fmtDate(d, dateOpts)
  const timePart = fmtTime(d, tz)
  const tzAbbrev = getTimezoneAbbreviation(d, tz)

  let result = `${datePart}, ${timePart} ${tzAbbrev}`

  // Add local conversion if different
  const localTimeStr = fmtTime(d, localTz)
  const localTzAbbr = getTimezoneAbbreviation(d, localTz)
  if (timePart !== localTimeStr || tzAbbrev !== localTzAbbr) {
    result += ` (${localTimeStr} ${localTzAbbr} your time)`
  }

  return result
}

/**
 * Splits a UTC ISO timestamp into wall-clock `YYYY-MM-DD` and `HH:mm` fields
 * for the given IANA timezone. Used to prefill date/time inputs without the
 * browser's local timezone leaking in.
 */
export function parseISOToTimezoneFields(
  isoDateStr: string | null | undefined,
  timezone: string,
): { date: string; time: string } {
  if (!isoDateStr) return { date: '', time: '' }
  const d = new Date(isoDateStr)
  if (isNaN(d.getTime())) return { date: '', time: '' }

  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(d)
  const get = (t: string) => parts.find((p) => p.type === t)?.value || ''
  let hour = get('hour')
  if (hour === '24') hour = '00'
  return {
    date: `${get('year')}-${get('month')}-${get('day')}`,
    time: `${hour}:${get('minute')}`,
  }
}

/**
 * Converts wall-clock date/time fields plus an IANA timezone into a UTC ISO
 * instant, so the backend can store the correct moment regardless of the
 * admin's browser timezone.
 */
export function timezoneFieldsToISO(
  dateStr: string,
  timeStr: string,
  timezone: string,
): string {
  const time = timeStr || '00:00'
  const naive = new Date(`${dateStr}T${time}:00Z`)
  if (isNaN(naive.getTime())) return ''

  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(naive)
  const get = (t: string) => parseInt(parts.find((p) => p.type === t)?.value || '0', 10)
  let hour = get('hour')
  if (hour === 24) hour = 0
  const tzWallAsUTC = Date.UTC(get('year'), get('month') - 1, get('day'), hour, get('minute'), get('second'))

  const offset = tzWallAsUTC - naive.getTime()
  return new Date(naive.getTime() - offset).toISOString()
}

/**
 * Short zone abbreviation ("EST", "CDT") for a wall-clock date + time in an
 * IANA zone. Resolves the real instant first so DST is picked correctly for
 * that day. Falls back to the raw zone id if the inputs can't be resolved.
 */
export function getWallClockTzAbbreviation(
  dateStr: string,
  timeStr: string | null,
  timezone: string,
): string {
  try {
    const iso = timezoneFieldsToISO(dateStr.slice(0, 10), (timeStr || '12:00').slice(0, 5), timezone)
    if (!iso) return timezone
    return getTimezoneAbbreviation(new Date(iso), timezone) || timezone
  } catch {
    return timezone
  }
}

/**
 * Formats a wall-clock event date + time (as stored on ticket sales:
 * `event_date` YYYY-MM-DD and `event_time` HH:MM[:SS], both local to
 * `timezone`) without ever shifting it through UTC or the viewer's zone.
 *
 *   ('2026-12-13', '00:30:00', 'America/New_York')
 *     → "Sunday, December 13, 2026 · 12:30 AM EST"
 *
 * The calendar day is formatted from a UTC-noon anchor with timeZone 'UTC',
 * so it is the stored day for every viewer. The time is hand-formatted, and
 * the zone abbreviation is taken from the real instant so DST is right.
 * Without a time, only the date is returned (no abbreviation). Returns null
 * when `date` is missing.
 */
export function formatWallClockEventWhen(
  date: string | null | undefined,
  time: string | null | undefined,
  timezone: string | null | undefined,
): string | null {
  if (!date) return null
  const datePart = date.slice(0, 10)
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(datePart)
  let dateStr = datePart
  if (m) {
    const anchor = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12))
    dateStr = fmtDate(anchor, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
  }

  const t = time ? /^(\d{1,2}):(\d{2})/.exec(time) : null
  if (!t) return dateStr
  const h = Number(t[1])
  const min = Number(t[2])
  if (h > 23 || min > 59) return dateStr
  const timeStr = fmtTime(Date.UTC(2000, 0, 1, h, min), 'UTC')

  let abbrev = ''
  if (timezone && m) {
    try {
      const iso = timezoneFieldsToISO(datePart, `${String(h).padStart(2, '0')}:${t[2]}`, timezone)
      if (iso) abbrev = getTimezoneAbbreviation(new Date(iso), timezone)
    } catch {
      // Unknown IANA zone — show the wall-clock time without an abbreviation.
    }
  }
  return `${dateStr} · ${timeStr}${abbrev ? ` ${abbrev}` : ''}`
}

/**
 * Formats a datetime with timezone abbreviation only (no local conversion).
 * Used in admin displays where "your time" conversion isn't needed.
 */
export function formatDateTimeWithTz(
  isoDateStr: string | null,
  eventTimezone: string | null,
  dateOptions?: Intl.DateTimeFormatOptions,
): string {
  if (!isoDateStr) return 'TBD'

  const d = new Date(isoDateStr)
  if (isNaN(d.getTime())) return 'TBD'

  const tz = eventTimezone || 'America/Chicago'

  const opts: Intl.DateTimeFormatOptions = dateOptions || {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }
  opts.timeZone = tz

  const formatted = fmtDate(d, opts)
  const tzAbbrev = getTimezoneAbbreviation(d, tz)

  return `${formatted} ${tzAbbrev}`
}
